import React, { Component } from "react";
import axios from "axios";
import {
  withStyles,
  Box,
  Grid,
  Typography,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  TablePagination,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { OpenInNew, ExpandMore } from "@material-ui/icons";
import { URL } from "../../config/config";

const STATUS_COLORS = {
  approved: { bg: "#e8f5e9", text: "#2e7d32" },
  rejected: { bg: "#ffebee", text: "#c62828" },
  pending: { bg: "#fff8e1", text: "#f57f17" },
};

const fmt = (date) =>
  date
    ? new Date(date).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
    : "—";

const rewardLabel = (currency) => (currency === "USD" ? "$20" : "₹1500");

class ShopifyRewardsPage extends Component {
  state = {
    rewardType: "shopify",
    claims: [],
    total: 0,
    page: 0,
    rowsPerPage: 15,
    tabValue: "all", // all | pending | approved | rejected
    search: "",
    isLoading: false,
    expandedClaimId: null,
    actionLoading: false,
    fetchReviewLoading: false,
    rejectDialogOpen: false,
    rejectClaimId: null,
    rejectAssistantId: null,
    rejectReason: "",
    alert: false,
    alertSeverity: "success",
    alertMsg: "",
  };

  componentDidMount() {
    this.fetchClaims();
  }

  fetchClaims = async () => {
    const { page, rowsPerPage, tabValue, search } = this.state;
    this.setState({ isLoading: true });
    try {
      const { data } = await axios.get(
        `${URL}/superadmin/shopify-reward-claims`,
        {
          params: {
            page: page + 1,
            limit: rowsPerPage,
            ...(tabValue !== "all" ? { status: tabValue } : {}),
            ...(search.trim() ? { search: search.trim() } : {}),
          },
        }
      );
      this.setState({ claims: data.claims || [], total: data.total || 0 });
    } catch (error) {
      this.setState({
        alert: true,
        alertSeverity: "error",
        alertMsg: error?.response?.data?.message || "Failed to fetch claims",
      });
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleTabs = (event, newValue) => {
    this.setState(
      { tabValue: newValue, page: 0, expandedClaimId: null },
      this.fetchClaims
    );
  };

  handleEnter = (e) => {
    if (e.key === "Enter") {
      this.setState({ page: 0 }, this.fetchClaims);
    }
  };

  applyFilter = () => {
    this.setState({ page: 0 }, this.fetchClaims);
  };

  resetFilter = () => {
    this.setState(
      { search: "", tabValue: "all", page: 0, expandedClaimId: null },
      this.fetchClaims
    );
  };

  fetchReviewForClaim = async (claim) => {
    this.setState({ fetchReviewLoading: true });
    try {
      const { data } = await axios.post(
        `${URL}/superadmin/shopify-reward-claim/${claim._id}/fetch-review`,
        { assistantId: claim.assistantId }
      );
      this.setState({
        alert: true,
        alertSeverity: data.success ? "success" : "warning",
        alertMsg: data.message,
      });
      if (data.success) await this.fetchClaims();
    } catch (error) {
      this.setState({
        alert: true,
        alertSeverity: "error",
        alertMsg: error?.response?.data?.message || "Failed to fetch comment",
      });
    } finally {
      this.setState({ fetchReviewLoading: false });
    }
  };

  approveClaim = async (claim) => {
    this.setState({ actionLoading: true });
    try {
      const { data } = await axios.post(
        `${URL}/superadmin/shopify-reward-claim/${claim._id}/approve`,
        { assistantId: claim.assistantId }
      );
      this.setState({
        alert: true,
        alertSeverity: "success",
        alertMsg: data.message,
      });
      await this.fetchClaims();
    } catch (error) {
      this.setState({
        alert: true,
        alertSeverity: "error",
        alertMsg: error?.response?.data?.message || "Failed to approve claim",
      });
    } finally {
      this.setState({ actionLoading: false });
    }
  };

  rejectClaim = async () => {
    const { rejectClaimId, rejectAssistantId, rejectReason } = this.state;
    if (!rejectReason.trim()) return;
    this.setState({ actionLoading: true });
    try {
      const { data } = await axios.post(
        `${URL}/superadmin/shopify-reward-claim/${rejectClaimId}/reject`,
        { rejectionReason: rejectReason.trim(), assistantId: rejectAssistantId }
      );
      this.setState({
        alert: true,
        alertSeverity: "success",
        alertMsg: data.message,
        rejectDialogOpen: false,
        rejectClaimId: null,
        rejectAssistantId: null,
        rejectReason: "",
      });
      await this.fetchClaims();
    } catch (error) {
      this.setState({
        alert: true,
        alertSeverity: "error",
        alertMsg: error?.response?.data?.message || "Failed to reject claim",
      });
    } finally {
      this.setState({ actionLoading: false });
    }
  };

  renderClaimDetails = (claim) => {
    const { classes } = this.props;
    const { actionLoading } = this.state;
    return (
      <Box className={classes.detailBox}>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Typography variant="caption" className={classes.fieldLabel}>
              Assistant ID
            </Typography>
            <Typography variant="body2">{claim.assistantId}</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="caption" className={classes.fieldLabel}>
              Shop Name
            </Typography>
            <Typography variant="body2">{claim.shopName || "N/A"}</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="caption" className={classes.fieldLabel}>
              Shop URL
            </Typography>
            <Typography variant="body2">{claim.shop || "N/A"}</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="caption" className={classes.fieldLabel}>
              WCC Credited
            </Typography>
            <Typography variant="body2">
              {claim.wccCredited
                ? `Yes — ${rewardLabel(claim.currency)} WCC`
                : "No"}
            </Typography>
          </Grid>

          {/* Comment not found (yet) — crawler ran in the background */}
          {!claim.userReview?.reviewId && (
            <Grid item xs={12}>
              <Typography variant="caption" className={classes.fieldLabel}>
                Review
              </Typography>
              <Box className={classes.llmFallbackBox}>
                <Typography variant="body2" style={{ color: "#757575" }}>
                  Comment not found on the App Store listing yet. The review
                  may not be published — use "Fetch Comment Again" to re-check.
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Review found by the crawler */}
          {claim.userReview?.reviewId && (
            <Grid item xs={12}>
              <Typography variant="caption" className={classes.fieldLabel}>
                Review Found on App Store
              </Typography>
              <Box className={classes.reviewBox}>
                <Box
                  display="flex"
                  alignItems="center"
                  style={{ gap: "8px", marginBottom: "6px" }}
                >
                  <Typography variant="body2" style={{ fontWeight: "bold" }}>
                    {claim.userReview.storeName}
                  </Typography>
                  <Typography variant="body2" style={{ color: "#f57f17" }}>
                    {"★".repeat(Math.round(claim.userReview.rating || 0))} (
                    {claim.userReview.rating}/5)
                  </Typography>
                  <Typography variant="caption" style={{ color: "#757575" }}>
                    {fmt(claim.userReview.reviewDate)}
                  </Typography>
                  {claim.userReview.reviewUrl && (
                    <a
                      href={claim.userReview.reviewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <OpenInNew
                        style={{ fontSize: "13px", color: "#1976d2" }}
                      />
                    </a>
                  )}
                </Box>
                <Typography
                  variant="body2"
                  style={{ color: "#424242", fontStyle: "italic" }}
                >
                  "{claim.userReview.content}"
                </Typography>
              </Box>
            </Grid>
          )}

          {/* LLM verdict / fallback */}
          {claim.userReview?.reviewId && !claim.llmResponse?.status && (
            <Grid item xs={12}>
              <Typography variant="caption" className={classes.fieldLabel}>
                AI Verdict
              </Typography>
              <Box className={classes.llmFallbackBox}>
                <Typography variant="body2" style={{ color: "#757575" }}>
                  AI verdict not available yet — it may still be processing
                  (refresh to check). If it never appears, verify the review
                  manually before approving.
                </Typography>
              </Box>
            </Grid>
          )}
          {claim.llmResponse?.status && (
            <Grid item xs={12}>
              <Typography variant="caption" className={classes.fieldLabel}>
                AI Verdict
              </Typography>
              <Box className={classes.llmBox}>
                <Typography variant="body2">
                  <b style={{ textTransform: "uppercase" }}>
                    {claim.llmResponse.status}
                  </b>
                  {claim.llmResponse.confidenceScore != null &&
                    ` — ${claim.llmResponse.confidenceScore}% confidence`}
                </Typography>
                {claim.llmResponse.rejectionReason && (
                  <Typography variant="body2" style={{ color: "#c62828" }}>
                    {claim.llmResponse.rejectionReason}
                  </Typography>
                )}
              </Box>
            </Grid>
          )}

          {claim.reviewedBy && (
            <Grid item xs={6}>
              <Typography variant="caption" className={classes.fieldLabel}>
                Reviewed By
              </Typography>
              <Typography variant="body2">{claim.reviewedBy}</Typography>
            </Grid>
          )}
          {claim.reviewedAt && (
            <Grid item xs={6}>
              <Typography variant="caption" className={classes.fieldLabel}>
                Reviewed At
              </Typography>
              <Typography variant="body2">{fmt(claim.reviewedAt)}</Typography>
            </Grid>
          )}
          {claim.status === "rejected" && claim.rejectionReason && (
            <Grid item xs={12}>
              <Typography variant="caption" className={classes.fieldLabel}>
                Rejection Reason
              </Typography>
              <Typography variant="body2" style={{ color: "#c62828" }}>
                {claim.rejectionReason}
              </Typography>
            </Grid>
          )}

          {claim.status === "pending" && (
            <Grid item xs={12}>
              <Box
                display="flex"
                justifyContent="flex-end"
                style={{ gap: "8px" }}
              >
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  disabled={
                    !!claim.userReview?.reviewId ||
                    this.state.fetchReviewLoading ||
                    actionLoading
                  }
                  onClick={() => this.fetchReviewForClaim(claim)}
                >
                  {this.state.fetchReviewLoading ? (
                    <CircularProgress size={16} />
                  ) : (
                    "Fetch Comment Again"
                  )}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  className={classes.rejectBtn}
                  disabled={actionLoading}
                  onClick={() =>
                    this.setState({
                      rejectDialogOpen: true,
                      rejectClaimId: claim._id,
                      rejectAssistantId: claim.assistantId,
                      rejectReason: "",
                    })
                  }
                >
                  Reject
                </Button>
                <Button
                  size="small"
                  color="primary"
                  variant="contained"
                  disabled={actionLoading}
                  onClick={() => this.approveClaim(claim)}
                >
                  {actionLoading ? (
                    <CircularProgress size={16} />
                  ) : (
                    `Approve & Credit ${rewardLabel(claim.currency)} WCC`
                  )}
                </Button>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  };

  render() {
    const { classes } = this.props;
    const {
      claims,
      total,
      page,
      rowsPerPage,
      tabValue,
      search,
      isLoading,
      expandedClaimId,
      rejectDialogOpen,
      rejectReason,
      actionLoading,
    } = this.state;

    return (
      <div className={classes.root}>
        {/* Page name & filters container */}
        <Grid
          container
          alignItems="center"
          className={`${classes.pageTitleContainer} ${classes.fullWidth}`}
          spacing={2}
        >
          <Grid item>
            <Box pl={{ xs: 2, md: 3 }}>
              <Typography variant="h3">Rewards</Typography>
            </Box>
          </Grid>
          <Grid item>
            <TextField
              select
              name="rewardType"
              value={this.state.rewardType}
              onChange={(e) => this.setState({ rewardType: e.target.value })}
              style={{ width: "170px" }}
            >
              <MenuItem value="shopify">Shopify Rewards</MenuItem>
            </TextField>
          </Grid>
          <Grid item>
            <TextField
              name="search"
              placeholder="Search assistant ID / shop / store"
              className={classes.textField}
              value={search}
              onChange={(e) => this.setState({ search: e.target.value })}
              onKeyPress={this.handleEnter}
            />
          </Grid>
          <Grid item>
            <Button color="primary" variant="contained" onClick={this.applyFilter}>
              Apply
            </Button>
          </Grid>
          <Grid item>
            <Button color="primary" variant="outlined" onClick={this.resetFilter}>
              Reset
            </Button>
          </Grid>
        </Grid>
        <Box my={3} />

        {/* Layout — rendered per selected reward type (only Shopify for now) */}
        {this.state.rewardType === "shopify" && (
        <Grid container justify="center">
          <Grid item xs={12} md={11} lg={10}>
            <Tabs
              value={tabValue}
              onChange={this.handleTabs}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
            >
              <Tab value="all" label="All" />
              <Tab value="pending" label="Immediate Action / Pending" />
              <Tab value="approved" label="Approved" />
              <Tab value="rejected" label="Rejected" />
            </Tabs>

            {/* Scrollable claims container */}
            <div className={classes.tableContainer}>
              <div className={classes.claimsScroller}>
                {/* Column headings */}
                <Box display="flex" alignItems="center" className={classes.headerRow}>
                  <Typography variant="body2" className={`${classes.colStatus} ${classes.headerCell}`}>
                    Status
                  </Typography>
                  <Typography variant="body2" className={`${classes.colAssistant} ${classes.headerCell}`}>
                    Assistant ID
                  </Typography>
                  <Typography variant="body2" className={`${classes.colShopName} ${classes.headerCell}`}>
                    Shop Name
                  </Typography>
                  <Typography variant="body2" className={`${classes.colShopUrl} ${classes.headerCell}`}>
                    Shop URL
                  </Typography>
                  <Typography variant="body2" className={`${classes.colDate} ${classes.headerCell}`}>
                    Date
                  </Typography>
                  <span className={classes.colChevron} />
                </Box>

                {isLoading ? (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    style={{ gap: "8px", padding: "60px 0" }}
                  >
                    <CircularProgress size={22} />
                    <Typography variant="body2" style={{ color: "#9e9e9e" }}>
                      Fetching claims...
                    </Typography>
                  </Box>
                ) : claims.length === 0 ? (
                  <Box style={{ padding: "60px 0", textAlign: "center" }}>
                    <Typography variant="body2" style={{ color: "#9e9e9e" }}>
                      No claims found.
                    </Typography>
                  </Box>
                ) : (
                  claims.map((claim, idx) => {
                    const isExpanded = expandedClaimId === claim._id;
                    const colors =
                      STATUS_COLORS[claim.status] || STATUS_COLORS.pending;
                    return (
                      <Box
                        key={claim._id}
                        className={isExpanded ? classes.expandedWrapper : ""}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          onClick={() =>
                            this.setState({
                              expandedClaimId: isExpanded ? null : claim._id,
                            })
                          }
                          className={classes.claimRowHeader}
                          style={{
                            backgroundColor: isExpanded
                              ? "#EBF5F3"
                              : idx % 2
                                ? "rgb(250,250,250)"
                                : "white",
                          }}
                        >
                          <Box className={classes.colStatus}>
                            <span
                              className={classes.statusBadge}
                              style={{
                                backgroundColor: colors.bg,
                                color: colors.text,
                                border: `1px solid ${colors.text}33`,
                              }}
                            >
                              <span
                                className={classes.statusDot}
                                style={{ backgroundColor: colors.text }}
                              />
                              {claim.status}
                            </span>
                          </Box>
                          <Box className={classes.colAssistant}>
                            <span className={classes.idChip}>
                              {claim.assistantId}
                            </span>
                          </Box>
                          <Typography variant="body2" className={`${classes.colShopName} ${classes.shopNameCell}`}>
                            {claim.shopName || claim.userReview?.storeName || "—"}
                          </Typography>
                          <Typography variant="body2" className={classes.colShopUrl}>
                            {claim.shop || "—"}
                          </Typography>
                          <Typography variant="body2" className={classes.colDate}>
                            {fmt(claim.submittedAt)}
                          </Typography>
                          <span className={classes.colChevron}>
                            <ExpandMore
                              className={classes.chevronIcon}
                              style={{
                                transform: isExpanded
                                  ? "rotate(180deg)"
                                  : "rotate(0deg)",
                              }}
                            />
                          </span>
                        </Box>
                        {isExpanded && this.renderClaimDetails(claim)}
                      </Box>
                    );
                  })
                )}
              </div>
            </div>
          </Grid>
        </Grid>
        )}

        {/* Table footer fixed at bottom */}
        <div className={classes.fixedBottomContainer}>
          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[15, 30, 50]}
            onChangePage={(e, newPage) =>
              this.setState({ page: newPage }, this.fetchClaims)
            }
            onChangeRowsPerPage={(e) =>
              this.setState(
                { rowsPerPage: parseInt(e.target.value, 10), page: 0 },
                this.fetchClaims
              )
            }
          />
        </div>

        {/* Rejection reason dialog */}
        <Dialog
          open={rejectDialogOpen}
          onClose={() =>
            !actionLoading &&
            this.setState({
              rejectDialogOpen: false,
              rejectClaimId: null,
              rejectAssistantId: null,
              rejectReason: "",
            })
          }
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h6" style={{ fontWeight: "bold" }}>
              Reject Claim
            </Typography>
          </DialogTitle>
          <DialogContent dividers>
            <Box
              style={{
                border: "1px solid #ffe082",
                borderRadius: "6px",
                padding: "10px 14px",
                marginBottom: "14px",
                backgroundColor: "#fff8e1",
              }}
            >
              <Typography variant="body2" style={{ color: "#8d6e00" }}>
                ⚠️ This reason will be shown to the customer in their dashboard
                — keep it clear, polite, and actionable so they know how to fix
                the issue and apply again.
              </Typography>
            </Box>
            <TextField
              label="Rejection Reason"
              value={rejectReason}
              onChange={(e) => this.setState({ rejectReason: e.target.value })}
              fullWidth
              multiline
              minRows={3}
              variant="outlined"
              disabled={actionLoading}
              placeholder="Why is this claim being rejected?"
            />
          </DialogContent>
          <DialogActions style={{ padding: "12px 24px" }}>
            <Button
              disabled={actionLoading}
              onClick={() =>
                this.setState({
                  rejectDialogOpen: false,
                  rejectClaimId: null,
                  rejectAssistantId: null,
                  rejectReason: "",
                })
              }
            >
              Cancel
            </Button>
            <Button
              className={classes.rejectConfirmBtn}
              variant="contained"
              disabled={actionLoading || !rejectReason.trim()}
              onClick={this.rejectClaim}
            >
              {actionLoading ? (
                <CircularProgress size={16} style={{ color: "#fff" }} />
              ) : (
                "Reject Claim"
              )}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={this.state.alert}
          autoHideDuration={4000}
          onClose={() => this.setState({ alert: false })}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={() => this.setState({ alert: false })}
            severity={this.state.alertSeverity}
          >
            {this.state.alertMsg}
          </Alert>
        </Snackbar>
      </div>
    );
  }
}

const styles = (theme) => ({
  root: {
    width: "100%",
    height: "100%",
    background: "rgb(249,249,249)",
    boxSizing: "border-box",
    overflowX: "hidden",
    position: "relative",
  },
  pageTitleContainer: {
    position: "sticky",
    zIndex: 100,
    top: 0,
    height: 60,
    boxSizing: "border-box",
  },
  fullWidth: {
    width: "100%",
    background: "white",
  },
  textField: {
    width: 300,
  },
  tableContainer: {
    height: "calc(100vh - 70px - 60px - 40px - 48px)",
    width: "100%",
    boxSizing: "border-box",
    [theme.breakpoints.down("sm")]: {
      height: "calc(100vh - 70px - 50px - 10px - 50px - 48px)",
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      boxSizing: "border-box",
    },
  },
  claimsScroller: {
    height: "100%",
    overflowY: "auto",
    overflowX: "hidden",
    border: "1px solid lightgrey",
    borderTop: "none",
    background: "white",
    boxSizing: "border-box",
  },
  headerRow: {
    background: "rgb(252,252,252)",
    position: "sticky",
    top: 0,
    zIndex: 10,
    padding: "0 16px",
    height: "50px",
    borderBottom: "2px solid rgb(224,224,224)",
  },
  headerCell: {
    fontWeight: "bold",
    color: "#424242",
  },
  // shared column widths for header + rows
  colStatus: {
    width: "110px",
    minWidth: "110px",
    flexShrink: 0,
  },
  colAssistant: {
    width: "220px",
    minWidth: "220px",
    flexShrink: 0,
    color: "#616161",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    paddingRight: "12px",
    boxSizing: "border-box",
  },
  colShopName: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    paddingRight: "12px",
    boxSizing: "border-box",
    color: "#424242",
  },
  colShopUrl: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    paddingRight: "12px",
    boxSizing: "border-box",
    color: "#616161",
  },
  colDate: {
    width: "150px",
    minWidth: "150px",
    flexShrink: 0,
    color: "#757575",
    fontSize: "13px",
  },
  colChevron: {
    width: "28px",
    minWidth: "28px",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  chevronIcon: {
    fontSize: "20px",
    color: "#9e9e9e",
    transition: "transform 0.25s ease",
  },
  shopNameCell: {
    fontWeight: 600,
    color: "#212121",
  },
  idChip: {
    fontFamily: "Menlo, Consolas, monospace",
    fontSize: "12px",
    color: "#616161",
    background: "rgb(243,243,243)",
    border: "1px solid rgb(228,228,228)",
    borderRadius: "4px",
    padding: "2px 8px",
    display: "inline-block",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    boxSizing: "border-box",
  },
  claimRowHeader: {
    padding: "14px 16px",
    minHeight: "56px",
    boxSizing: "border-box",
    cursor: "pointer",
    borderBottom: "1px solid rgb(238,238,238)",
    transition: "background 0.2s ease",
    "&:hover": {
      background: "#EBF5F3 !important",
    },
  },
  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "3px 10px",
    borderRadius: "12px",
    fontWeight: 600,
    fontSize: "11px",
    letterSpacing: "0.4px",
    textTransform: "capitalize",
    boxSizing: "border-box",
  },
  statusDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    display: "inline-block",
    flexShrink: 0,
  },
  // expanded row group — clearly framed against the rest of the table
  expandedWrapper: {
    border: "2px solid #0a8f29",
    borderRadius: "4px",
    margin: "4px 6px",
    overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },
  detailBox: {
    padding: "16px 20px",
    borderTop: "1px solid rgb(224,224,224)",
    backgroundColor: "#fff",
  },
  fieldLabel: {
    color: "#757575",
    display: "block",
    marginBottom: "4px",
  },
  reviewBox: {
    border: "1px solid #c8e6c9",
    borderRadius: "6px",
    padding: "10px 14px",
    backgroundColor: "#f1f8e9",
  },
  llmBox: {
    border: "1px solid #bbdefb",
    borderRadius: "6px",
    padding: "10px 14px",
    backgroundColor: "#e3f2fd",
  },
  llmFallbackBox: {
    border: "1px solid #e0e0e0",
    borderRadius: "6px",
    padding: "10px 14px",
    backgroundColor: "#f5f5f5",
  },
  rejectBtn: {
    color: "#c62828",
    borderColor: "#c62828",
  },
  rejectConfirmBtn: {
    color: "#fff",
    backgroundColor: "#c62828",
    "&:hover": {
      backgroundColor: "#8e0000",
    },
  },
  fixedBottomContainer: {
    position: "fixed",
    height: "60px",
    borderTop: "1px solid lightgrey",
    background: "white",
    bottom: "0",
    left: "71px",
    right: "0",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      left: "0px",
      height: "50px",
    },
  },
});

export default withStyles(styles)(ShopifyRewardsPage);
